import React, { useEffect, useMemo, useState } from 'react';
import {
  submitFormSession,
  fetchFormSessionProfile,
  getFormSessionErrorMessage,
} from '../utils/formSessionApi';
import { useAuth0 } from '@auth0/auth0-react';
import BuyerAgreementForm, {
  agentPrefillFromUserProfile,
  mergeBuyerAgreementInitialValues,
  useBuyerAgreementAgentPrefill,
} from './BuyerAgreementForm';
import { trackFormWizardEvent, formWizardEventPayload } from '../utils/formWizardTelemetry';
import './ContractWizard.css';

/**
 * Shared BBSA wizard shell used by magic-link sessions and the KeyFlo portal.
 *
 * - formSessionToken set → submit via /forms/sessions/:token/submit
 * - portalOnSubmit set → portal create (POST /buyer-agreements/)
 * - mode: 'page' | 'modal' | 'embedded' (embedded = form only, parent provides chrome)
 */
const BuyerAgreementWizard = ({
  isOpen = true,
  onClose,
  mode = 'modal',
  initialValues = {},
  formSessionToken = null,
  portalOnSubmit = null,
  onSuccess = null,
  userEmail = null,
  licensedState: licensedStateProp = null,
  submitLabel = 'Create Buyers Agreement',
  onCancel,
  cancelLabel = 'Cancel',
  initialSuccess = false,
  wizardSteps = [],
  onBackToCatalog,
}) => {
  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const isPageMode = mode === 'page';
  const isEmbedded = mode === 'embedded';
  const visible = isPageMode || isEmbedded || isOpen;

  const portalPrefill = useBuyerAgreementAgentPrefill(
    portalOnSubmit ? userEmail : null,
    initialValues,
  );

  const [profilePrefill, setProfilePrefill] = useState({});
  const [licensedState, setLicensedState] = useState(
    licensedStateProp || initialValues.state || initialValues.licensed_state || 'OK',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(Boolean(initialSuccess));
  const [sessionReady, setSessionReady] = useState(!formSessionToken);

  useEffect(() => {
    if (!visible || formSessionToken) return undefined;
    const email = userEmail || auth0User?.email;
    if (!email) return undefined;
    trackFormWizardEvent(getAccessTokenSilently, formWizardEventPayload({
      phase: 'started',
      formType: 'buyer_agreement',
      userEmail: email,
      userName: auth0User?.name || auth0User?.nickname || '',
    })).catch(() => {});
    return undefined;
  }, [visible, formSessionToken, userEmail, auth0User, getAccessTokenSilently]);

  useEffect(() => {
    if (!visible || !formSessionToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchFormSessionProfile(formSessionToken);
        if (cancelled) return;
        const agentPrefill = agentPrefillFromUserProfile(profile);
        setProfilePrefill(agentPrefill);
        if (profile?.licensed_state) {
          setLicensedState(profile.licensed_state);
        }
      } catch {
        /* profile optional */
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [visible, formSessionToken]);

  useEffect(() => {
    if (portalPrefill.licensedState) {
      setLicensedState(portalPrefill.licensedState);
    }
  }, [portalPrefill.licensedState]);

  const mergedInitialValues = useMemo(() => {
    if (formSessionToken) {
      return mergeBuyerAgreementInitialValues(profilePrefill, initialValues);
    }
    if (portalOnSubmit) {
      return mergeBuyerAgreementInitialValues(portalPrefill.initialValues, initialValues);
    }
    return mergeBuyerAgreementInitialValues(initialValues);
  }, [formSessionToken, portalOnSubmit, profilePrefill, portalPrefill.initialValues, initialValues]);

  const handleSubmit = async (payload) => {
    try {
      setSaving(true);
      setError(null);
      const email = userEmail || auth0User?.email;

      if (formSessionToken) {
        await submitFormSession(formSessionToken, payload);
        setSuccess(true);
        onSuccess?.();
        return;
      }

      if (portalOnSubmit) {
        const result = await portalOnSubmit(payload);
        onSuccess?.(result);
        if (mode !== 'embedded') {
          setSuccess(true);
        }
        if (email) {
          trackFormWizardEvent(getAccessTokenSilently, formWizardEventPayload({
            phase: 'completed',
            formType: 'buyer_agreement',
            userEmail: email,
            userName: auth0User?.name || auth0User?.nickname || '',
            metadata: {
              buyer_agreement_id: result?.id || result?.buyer_agreement_id || result?.buyerAgreementId,
            },
          })).catch(() => {});
        }
        return;
      }
    } catch (err) {
      const portalMessage = err?.response?.data?.error;
      setError(
        portalMessage
        || getFormSessionErrorMessage(err, 'Failed to create buyer agreement'),
      );
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;

  const formNode = (
    <BuyerAgreementForm
      initialValues={mergedInitialValues}
      licensedState={licensedStateProp || licensedState}
      onSubmit={handleSubmit}
      saving={saving}
      error={error}
      submitLabel={submitLabel}
      onCancel={onCancel || (isEmbedded ? onClose : undefined)}
      cancelLabel={cancelLabel}
      stickyFooter={isPageMode || isEmbedded}
      stackedLayout={isPageMode || isEmbedded}
      wizardSteps={wizardSteps}
      onBackToCatalog={onBackToCatalog}
      valuesReady={(!portalOnSubmit || portalPrefill.ready) && sessionReady}
    />
  );

  if (isEmbedded) {
    return formNode;
  }

  const overlayClass = isPageMode ? 'wizard-overlay wizard-page-mode' : 'wizard-overlay';

  if (success) {
    return (
      <div className={overlayClass}>
        <div className="wizard-container">
          <div className="wizard-header"><h2>Buyers Agreement</h2></div>
          <div className="wizard-content ba-wizard-success">
            <div className="result-message success">✅ Buyers Agreement created successfully!</div>
            {formSessionToken && (
              <p className="ba-wizard-success-note">Check your email for the completed document.</p>
            )}
            <div className="ba-wizard-success-actions">
              <button className="wizard-button primary" type="button" onClick={() => onClose()}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={overlayClass}>
      <div className="wizard-container">
        <div className="wizard-header">
          <h2>Buyers Agreement</h2>
          <button className="wizard-close" type="button" onClick={() => onClose()}>×</button>
        </div>
        {formNode}
      </div>
    </div>
  );
};

export default BuyerAgreementWizard;
